/**
 * SiteInsightsPanel — V146.1.
 *
 * Surfaces deep-detection results from scan.site to wow the visitor:
 * "Wait, you know I'm on WooCommerce + Cloudflare + GA4?"
 *
 * Only renders categories with detected data — no empty placeholders.
 */
import { Server, Wrench, ShieldCheck, BarChart3, CreditCard, Truck, Globe, Hash, Building2 } from "lucide-react";

function Pill({ children, accent = "amber" }) {
  const cls = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    blue: "border-blue-500/40 bg-blue-500/10 text-blue-200",
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-200",
    purple: "border-purple-500/40 bg-purple-500/10 text-purple-200",
    cyan: "border-cyan-500/40 bg-cyan-500/10 text-cyan-200",
    lime: "border-lime-500/40 bg-lime-500/10 text-lime-200",
  }[accent];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${cls}`}>
      {children}
    </span>
  );
}

function Row({ Icon, label, children, testid }) {
  return (
    <div data-testid={testid} className="border border-gray-800 bg-neutral-950 p-4 flex items-start gap-3">
      <Icon className="w-4 h-4 mt-0.5 text-amber-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest font-black text-gray-500 mb-1.5">{label}</p>
        <div className="flex flex-wrap gap-1.5">{children}</div>
      </div>
    </div>
  );
}

export default function SiteInsightsPanel({ site }) {
  if (!site) return null;
  const has = (arr) => Array.isArray(arr) && arr.length > 0;
  const obj = (o) => o && Object.keys(o).length > 0;

  const platformValue = site.platform || site.cms;
  const showInsights = (
    platformValue
    || has(site.hosting) || has(site.framework)
    || has(site.analytics) || has(site.compliance)
    || has(site.payments) || has(site.shipping)
    || has(site.marketplaces) || obj(site.social) || obj(site.biz)
    || has(site.languages) || (site.seo && (site.seo.og_count || site.seo.schema_types?.length))
  );
  if (!showInsights) return null;

  return (
    <section className="mb-8" data-testid="site-insights-panel">
      <div className="border border-amber-500/30 bg-amber-500/5 p-5 sm:p-6 mb-4">
        <p className="text-[10px] uppercase tracking-[0.25em] font-black text-amber-400 mb-2">
          🔍 Čo o tvojom webe vieme
        </p>
        <p className="text-sm text-gray-300 leading-relaxed">
          Naskenoval/a som <strong className="text-white">{site.url}</strong> a tu je všetko, čo som našiel. Toto sú dáta, na ktorých sme postavili tvoj agent ranking.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Platform */}
        {platformValue ? (
          <Row Icon={Wrench} label="Platforma / CMS" testid="insight-platform">
            <Pill accent="amber">{platformValue}</Pill>
            {has(site.framework) ? site.framework.map(f => <Pill key={f} accent="cyan">{f}</Pill>) : null}
          </Row>
        ) : null}

        {/* Hosting */}
        {has(site.hosting) ? (
          <Row Icon={Server} label="Hosting / CDN" testid="insight-hosting">
            {site.hosting.map(h => <Pill key={h} accent="cyan">{h}</Pill>)}
            {site.server?.hsts ? <Pill accent="emerald">HSTS ✓</Pill> : null}
            {site.server?.http_version ? <Pill accent="cyan">{site.server.http_version}</Pill> : null}
          </Row>
        ) : null}

        {/* Analytics */}
        {has(site.analytics) ? (
          <Row Icon={BarChart3} label="Analytics & tracking" testid="insight-analytics">
            {site.analytics.map(a => <Pill key={a} accent="purple">{a}</Pill>)}
          </Row>
        ) : null}

        {/* Compliance */}
        {has(site.compliance) ? (
          <Row Icon={ShieldCheck} label="Compliance & GDPR" testid="insight-compliance">
            {site.compliance.map(c => <Pill key={c} accent="emerald">{c}</Pill>)}
          </Row>
        ) : null}

        {/* Payments */}
        {has(site.payments) ? (
          <Row Icon={CreditCard} label="Payment gateways" testid="insight-payments">
            {site.payments.map(p => <Pill key={p} accent="blue">{p}</Pill>)}
          </Row>
        ) : null}

        {/* Shipping */}
        {has(site.shipping) ? (
          <Row Icon={Truck} label="Doprava" testid="insight-shipping">
            {site.shipping.map(s => <Pill key={s} accent="amber">{s}</Pill>)}
          </Row>
        ) : null}

        {/* Marketplaces */}
        {has(site.marketplaces) ? (
          <Row Icon={Globe} label="Marketplaces" testid="insight-marketplaces">
            {site.marketplaces.map(m => <Pill key={m} accent="amber">{m}</Pill>)}
          </Row>
        ) : null}

        {/* Languages */}
        {has(site.languages) ? (
          <Row Icon={Globe} label="Jazyky (hreflang)" testid="insight-languages">
            {site.languages.map(l => <Pill key={l} accent="cyan">{l.toUpperCase()}</Pill>)}
          </Row>
        ) : null}

        {/* Social */}
        {obj(site.social) ? (
          <Row Icon={Hash} label="Sociálne profily" testid="insight-social">
            {Object.entries(site.social).map(([net, handle]) => (
              <Pill key={net} accent="rose">{net}/{String(handle).slice(0, 20)}</Pill>
            ))}
          </Row>
        ) : null}

        {/* Business */}
        {obj(site.biz) ? (
          <Row Icon={Building2} label="Firma" testid="insight-biz">
            {site.biz.ico ? <Pill accent="amber">IČO {site.biz.ico}</Pill> : null}
            {site.biz.dic ? <Pill accent="amber">DIČ {site.biz.dic}</Pill> : null}
            {site.biz.phone ? <Pill accent="emerald">📞 {site.biz.phone}</Pill> : null}
            {site.biz.postal ? <Pill accent="cyan">📍 {site.biz.postal}</Pill> : null}
          </Row>
        ) : null}

        {/* SEO */}
        {site.seo && (site.seo.og_count || site.seo.schema_types?.length || site.seo.hreflang_count) ? (
          <Row Icon={Hash} label="SEO signály" testid="insight-seo">
            {site.seo.og_count > 0 ? <Pill accent="lime">{site.seo.og_count} OG meta</Pill> : null}
            {site.seo.twitter_card ? <Pill accent="lime">Twitter card</Pill> : null}
            {site.seo.canonical ? <Pill accent="lime">Canonical</Pill> : null}
            {site.seo.hreflang_count > 0 ? <Pill accent="lime">{site.seo.hreflang_count} hreflang</Pill> : null}
            {site.seo.schema_types?.length > 0 ? <Pill accent="emerald">Schema: {site.seo.schema_types.slice(0, 3).join(", ")}</Pill> : null}
          </Row>
        ) : null}

        {/* Content metrics */}
        {site.content && (site.content.image_count || site.content.word_count) ? (
          <Row Icon={Hash} label="Obsah" testid="insight-content">
            {site.content.image_count > 0 ? <Pill accent="amber">{site.content.image_count} obrázkov</Pill> : null}
            {site.content.word_count > 0 ? <Pill accent="amber">{site.content.word_count} slov</Pill> : null}
            {site.content.has_blog ? <Pill accent="emerald">Blog ✓</Pill> : null}
            {site.content.has_newsletter_signup ? <Pill accent="emerald">Newsletter ✓</Pill> : null}
          </Row>
        ) : null}
      </div>
    </section>
  );
}
